/**
 * Agent taxonomy — DISPLAY rules for the Flow Validator (ADR-103 D1/D2/D5/D9a).
 *
 * The classification itself happens once, at write time, in edge-ingest: the
 * User-Agent is cross-checked against the network's AS-organisation and the
 * verdict is stored on the row. Nothing here re-derives it. This module only
 * decides how an already-classified row should READ on screen.
 *
 * That separation is the whole point of the taxonomy: the User-Agent exists
 * only on the live request. It is read at the edge and discarded, so a row
 * written before the taxonomy shipped can never be classified retroactively —
 * which is why an untagged row renders as "Legacy (untagged)" rather than
 * being guessed at.
 */
import type { Channel, TelemetryEvent, TelemetrySession } from './flow-validator-api';

/** The four write-time classes, plus the historical untagged case. */
export type AgentClass = 'human' | 'llm' | 'crawler' | 'synthetic';

/** A row carrying enough taxonomy to be displayed — events and sessions both
 *  qualify, so every helper here works on either without duplication. */
type TaxonomyRow = Pick<TelemetryEvent | TelemetrySession, 'agentClass' | 'source'> & {
  channel?: Channel;
};

export const AGENT_CLASS_KEYS: AgentClass[] = ['human', 'llm', 'crawler', 'synthetic'];

export const AGENT_CLASS_LABEL: Record<AgentClass, string> = {
  human: 'Human',
  llm: 'LLM',
  crawler: 'Crawler',
  synthetic: 'Synthetic',
};

/**
 * Vendor slug → display name. Mirrors the reporting service's map so the same
 * bot reads identically on the board, in a report and on this screen.
 * Capitalisation is the vendors' own (ClaudeBot, GPTBot, Googlebot).
 */
const AGENT_DISPLAY_NAME: Record<string, string> = {
  claudebot: 'ClaudeBot',
  gptbot: 'GPTBot',
  'oai-searchbot': 'OAI-SearchBot',
  'chatgpt-user': 'ChatGPT-User',
  perplexity: 'PerplexityBot',
  googlebot: 'Googlebot',
  'google-extended': 'Google-Extended',
  bingbot: 'Bingbot',
  ahrefs: 'AhrefsBot',
  semrush: 'SemrushBot',
  applebot: 'Applebot',
  amazonbot: 'Amazonbot',
  bytespider: 'Bytespider',
  monitor: 'Monitor',
  'embed-probe': 'embed-probe',
  'submit-probe': 'submit-probe',
  'test-beacon': 'test-beacon',
};

/** Vendor slug → display name; unknown slugs are Title-cased rather than
 *  hidden, so a newly-seen crawler is still legible while the map catches up. */
export function agentDisplayName(slug: string): string {
  if (!slug) return '';
  const known = AGENT_DISPLAY_NAME[slug];
  if (known) return known;
  return slug.charAt(0).toUpperCase() + slug.slice(1);
}

/** The row's class, or null for a historical untagged row. Never infers. */
export function agentClassOf(row: TaxonomyRow): AgentClass | null {
  const c = row.agentClass;
  return (AGENT_CLASS_KEYS as string[]).includes(c) ? (c as AgentClass) : null;
}

/**
 * True when the row is a machine rather than a person.
 *
 * `source === 'synthetic'` is included as well as `agentClass === 'synthetic'`
 * because probes deployed before the taxonomy shipped set only the source. A
 * probe is a machine either way, and reading one as a visitor would put fake
 * traffic into the human slice.
 */
export function isMachineRow(row: TaxonomyRow): boolean {
  const c = row.agentClass;
  return c === 'llm' || c === 'crawler' || c === 'synthetic' || row.source === 'synthetic';
}

/**
 * Channel as the Flow Validator DISPLAYS it (D2).
 *
 * Machine rows show "—". Channel describes how a HUMAN arrived; a crawler was
 * not acquired from anywhere, so attributing one to "Direct" — which is what
 * the pre-taxonomy build did — is a small, repeated lie that also inflated the
 * Direct bucket with bot traffic.
 */
export const NO_CHANNEL = '—';

export function channelDisplay(row: TaxonomyRow, channelLabel: string): string {
  return isMachineRow(row) ? NO_CHANNEL : channelLabel;
}

/**
 * Internal / external grouping (D5) — DERIVED for display, never stored.
 *
 * `internal` means PLATFORM-GENERATED: our own synthetic producers, and
 * nothing else. Staff browsing the product deliberately stays EXTERNAL `live`.
 * That is a product stance rather than an oversight — staff must experience
 * telemetry exactly as a customer does, and a staff-only carve-out would hide
 * the very rows that make a defect visible during UAT.
 */
export type Scope = 'external' | 'internal';

export const SCOPE_KEYS: Scope[] = ['external', 'internal'];
export const SCOPE_LABEL: Record<Scope, string> = {
  external: 'External',
  internal: 'Internal',
};

export function scopeOf(row: TaxonomyRow): Scope {
  return row.source === 'synthetic' || row.agentClass === 'synthetic' ? 'internal' : 'external';
}

/**
 * The D9a noise filter: a session with exactly one pageview and zero dwell.
 *
 * DISPLAY-ONLY, and that constraint is load-bearing. These sessions are real
 * rows — mostly machine reads, which are legitimately one page and zero
 * seconds — so hiding them must never change a count, a pipe or a metric. The
 * filter pill counts above the table are computed BEFORE this is applied, so
 * toggling it moves nothing but what is on screen.
 */
export function isOnePageZeroDwell(session: TelemetrySession): boolean {
  return session.pageCount <= 1 && session.durationSeconds === 0;
}
